import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  IssueScoFiscalHoldCommand,
  ScoFiscalHoldCommand,
  ScoFiscalHoldView,
  ScoFiscalOpenHoldListView,
} from '@reform-lite/contracts';
import { DASHBOARD_SUMMARY_KEY } from '@/hooks/use-dashboard';
import { apiClient } from '@/lib/api-client';

const laneKey = (terminalId: string) => ['sco-fiscal-holds', terminalId] as const;
const openKey = ['sco-fiscal-holds', 'open'] as const;

/** EAT-25: a lane's held fiscal sales and their commands, as it last reported them. */
export function useScoFiscalHolds(terminalId: string) {
  return useQuery({
    queryKey: laneKey(terminalId),
    refetchInterval: 15_000,
    queryFn: async (): Promise<ScoFiscalHoldView> => {
      const response = await apiClient.get<ScoFiscalHoldView>('/sco/fiscal-holds', {
        params: { terminalId },
      });
      return response.data;
    },
  });
}

/** EAT-25: every open hold on every lane of the tenant, oldest first. */
export function useOpenScoFiscalHolds() {
  return useQuery({
    queryKey: openKey,
    refetchInterval: 30_000,
    queryFn: async (): Promise<ScoFiscalOpenHoldListView> => {
      const response = await apiClient.get<ScoFiscalOpenHoldListView>('/sco/fiscal-holds/open');
      return response.data;
    },
  });
}

/**
 * EAT-25: order a lane to retry or void one held sale; the lane collects it on
 * its next report. A 401 `reauth_required` asks for the password (step-up),
 * never a sign-out (see `lib/api-client.ts`).
 */
export function useIssueScoFiscalHold() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: IssueScoFiscalHoldCommand): Promise<ScoFiscalHoldCommand> => {
      const response = await apiClient.post<ScoFiscalHoldCommand>(
        '/sco/fiscal-holds/commands',
        input,
      );
      return response.data;
    },
    // Re-read in every case: a refusal (`phase_changed`) means the view is old.
    onSettled: async (_data, _error, input) => {
      await queryClient.invalidateQueries({ queryKey: laneKey(input.terminalId) });
      await queryClient.invalidateQueries({ queryKey: openKey });
      // The dashboard tile counts the same holds.
      await queryClient.invalidateQueries({ queryKey: DASHBOARD_SUMMARY_KEY });
    },
  });
}
